import {
  makeStyles,
  Grid,
  Container,
  Button,
  Typography,
  Divider,
  FormLabel,
} from '@material-ui/core';
import React from 'react';
import { useHistory } from 'react-router';
import complicationPrediction from '../Calculations/complicationPrediction';
import surgicalApproaches from '../Enums/SurgicalApproaches';
import AsaClassControl from './Controls/AsaClassControl';
import BmiEntryControl from './Controls/BmiControl';
import SurgicalApproachControl from './Controls/SurgicalApproachControl';
import TextEntryControl from './Controls/TextEntryControl';
import YesNoChoiceControl from './Controls/YesNoChoiceControl';
import Prediction from './Prediction';
import PrintIntroduction from './PrintIntroduction';

const useStyles = makeStyles((theme) => ({
  divider: {
    marginTop: theme.spacing(2),
  },
  introText: {
    fontSize: '1.1em',
  },
  prediction: {
    padding: '16px',
    border: '2px solid black',
    borderRadius: '20px',
    maxWidth: '750px',
    height: '100%',
  },
  secondaryPrediction: {
    padding: '16px',
    border: '2px solid black',
    borderRadius: '20px',
    maxWidth: '750px',
    backgroundColor: '#F3F5F9',
    height: '100%',
  },
}));

const heightToInches = (feet: number, inches: number): number => (feet * 12 + inches);

export default function Home(): JSX.Element {
  const history = useHistory();
  const classes = useStyles();
  const [prediction, setPrediction] = React.useState<
  { primary: number | null, secondary: number | null }>({
    primary: null,
    secondary: null,
  });
  const [formState, setFormState] = React.useState({
    age: '',
    prevCaesarean: '',
    surgicalApproach: '',
    preoperativeHb: '',
    asaClass: '',
    intraEndometriosis: '',
    intraAdhesions: '',
    buttonName: 'display',
  });
  const [bmiState, setBmiState] = React.useState({
    heightFeet: '',
    heightInch: '',
    weight: '',
  });
  const [errorState, setErrorState] = React.useState({
    age: false,
    prevCaesarean: false,
    surgicalApproach: false,
    preoperativeHb: false,
    asaClass: false,
    intraEndometriosis: false,
    intraAdhesions: false,
  });

  const handleChange = (event: React.ChangeEvent<{ name?: string | undefined,
    value: unknown }>): void => {
    setFormState({ ...formState, [event.target.name as string]: event.target.value });
    setErrorState({ ...errorState, [event.target.name as string]: false });
  };

  const handleBmiChange = (event: React.ChangeEvent<{ name?: string | undefined,
    value: unknown }>): void => {
    setBmiState({ ...bmiState, [event.target.name as string]: event.target.value });
    setErrorState({ ...errorState, [event.target.name as string]: false });
  };

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setFormState({ ...formState, [event.target.name]: event.target.value });
    setErrorState({ ...errorState, [event.target.name as string]: false });
  };

  const patientBmi = (): number => (((+bmiState.weight)
    / heightToInches(+bmiState.heightFeet, +bmiState.heightInch) ** 2) * 703);

  const submitForm = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    setErrorState({
      age: !formState.age,
      prevCaesarean: !formState.prevCaesarean,
      surgicalApproach: !formState.surgicalApproach,
      preoperativeHb: !formState.preoperativeHb,
      asaClass: !formState.asaClass,
      intraEndometriosis: !formState.intraEndometriosis,
      intraAdhesions: !formState.intraAdhesions,
    });
    if (!Object.values(formState).every(Boolean)) {
      setPrediction({ primary: null, secondary: null });
      return;
    }
    const surgicalApproach = surgicalApproaches
      .find((approach) => approach === formState.surgicalApproach);
    const riskPercentage = complicationPrediction(+formState.age, formState.prevCaesarean === 'true',
      surgicalApproach || surgicalApproaches[1], +(formState.asaClass || '1'),
      +formState.preoperativeHb, formState.intraEndometriosis === 'true',
      formState.intraAdhesions === 'true');

    if (formState.buttonName === 'display') {
      setPrediction(riskPercentage);
    } else if (formState.buttonName === 'printOut') {
      history.push({
        pathname: '/printout',
        state: {
          prediction: riskPercentage,
        },
      });
    }
  };

  return (
    <form autoComplete="off" onSubmit={submitForm}>
      <Container maxWidth="lg">
        <Grid container spacing={2} style={{ padding: '36px' }}>
          <Grid item xs={12}>
            <img src={`${process.env.PUBLIC_URL}/images/sinai-logo.png`} alt="Sinai Health logo" style={{ paddingTop: '20px' }} />
          </Grid>
          <Grid item xs={12}>
            <PrintIntroduction className={classes.introText} />
          </Grid>
          <Grid item container spacing={8} justify="center">
            <Grid item container spacing={2} xs={12} sm={6}>
              <Grid item xs={12}>
                <TextEntryControl
                  title="What is your patient's age?"
                  placeholder="Enter age"
                  units="years"
                  name="age"
                  value={formState.age}
                  error={errorState.age}
                  errorText="You must enter your patient's age"
                  handleChange={handleChange}
                />
                <Divider className={classes.divider} />
              </Grid>
              <Grid container item xs={12}>
                <Grid item xs={12}>
                  <FormLabel
                    component="legend"
                    style={{
                      color: 'black', fontWeight: 'bold', paddingBottom: '0.75em',
                    }}
                  >
                    What is your patient&lsquo;s height?
                  </FormLabel>
                </Grid>
                <Grid item style={{ padding: '0.25em' }}>
                  <TextEntryControl
                    units="feet"
                    name="heightFeet"
                    value={bmiState.heightFeet}
                    handleChange={handleBmiChange}
                  />
                </Grid>
                <Grid item style={{ padding: '0.25em' }}>
                  <TextEntryControl
                    units="inches"
                    name="heightInch"
                    value={bmiState.heightInch}
                    handleChange={handleBmiChange}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <TextEntryControl
                  title="What is your patient's weight?"
                  units="lbs"
                  name="weight"
                  value={bmiState.weight}
                  handleChange={handleBmiChange}
                />
              </Grid>
              <Grid container item xs={12} justify="space-between">
                <Grid item>
                  <Typography style={{ fontWeight: 'bold' }}>Your patient&rsquo;s Body Mass Index (BMI) is:</Typography>
                </Grid>
                <Grid item>
                  <BmiEntryControl
                    units="kg/m²"
                    name="bmis"
                    value={`${Number.isNaN(patientBmi()) ? '---' : patientBmi().toFixed(2)}`}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Divider className={classes.divider} />
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <TextEntryControl
                  title="What is your patient's Hemoglobin (Hb)?"
                  placeholder="Enter Hb"
                  units="g/L"
                  name="preoperativeHb"
                  value={formState.preoperativeHb}
                  error={errorState.preoperativeHb}
                  errorText="You must enter your patient's Hemoglobin"
                  handleChange={handleChange}
                />
                <Divider className={classes.divider} />
              </Grid>
              <Grid item xs={12}>
                <YesNoChoiceControl
                  title="Has your patient had a previous caesarean section?"
                  name="prevCaesarean"
                  value={formState.prevCaesarean}
                  handleChange={handleRadioChange}
                  error={errorState.prevCaesarean}
                />
                <Divider className={classes.divider} />
              </Grid>
              <Grid item xs={12}>
                <SurgicalApproachControl
                  value={formState.surgicalApproach || ''}
                  handleChange={handleRadioChange}
                  error={errorState.surgicalApproach}
                />
              </Grid>
            </Grid>
            <Grid item container spacing={2} xs={12} sm={6}>
              <Grid item xs={12}>
                <AsaClassControl
                  value={formState.asaClass}
                  handleChange={handleRadioChange}
                  error={errorState.asaClass}
                />
                <Divider className={classes.divider} />
              </Grid>
              <Grid item xs={12}>
                <YesNoChoiceControl
                  title="Do you suspect intraoperative endometriosis?"
                  name="intraEndometriosis"
                  value={formState.intraEndometriosis}
                  handleChange={handleRadioChange}
                  error={errorState.intraEndometriosis}
                />
                <Divider className={classes.divider} />
              </Grid>
              <Grid item xs={12}>
                <YesNoChoiceControl
                  title="Do you suspect intraoperative adhesions?"
                  name="intraAdhesions"
                  value={formState.intraAdhesions}
                  handleChange={handleRadioChange}
                  error={errorState.intraAdhesions}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid container item xs={12} justify="space-between" spacing={2}>
            <Grid item>
              <Button
                type="submit"
                variant="contained"
                color="secondary"
                style={{ fontSize: '0.95rem' }}
                onClick={() => setFormState({ ...formState, buttonName: 'display' })}
              >
                Result
              </Button>
            </Grid>
            <Grid item>
              <Button
                type="submit"
                variant="contained"
                color="secondary"
                style={{ textTransform: 'none', fontSize: '0.95rem' }}
                onClick={() => setFormState({ ...formState, buttonName: 'printOut' })}
              >
                Print Patient Handout
              </Button>
            </Grid>
          </Grid>
          <Grid container item xs={12} justify="flex-start" spacing={2}>
            <Grid item sm={6} xs={12}>
              {prediction.primary
              && (
              <Prediction
                title="Based on this model, your patient's personalized risk of a Grade ≥ 2 complication is:"
                prediction={prediction.primary}
                className={classes.prediction}
              />
              )}
            </Grid>
            {prediction.secondary
              && (
              <Grid item sm={6} xs={12}>
                <Prediction
                  title="Your patient currently has preoperative anemia. If this is corrected, your patient's risk of a Grade ≥ 2 complication is:"
                  prediction={prediction.secondary}
                  className={classes.secondaryPrediction}
                />
              </Grid>
              )}
          </Grid>
        </Grid>
      </Container>
    </form>
  );
}
